/**
 * Resume de sesión Tiger al abrir LoginPortal (cold start / reopen).
 * Same-origin: validate + refresh vía BFF WMS. Multi-server: redirect al tenant persistido.
 *
 * Anti-loop: Tiger WebView a menudo borra sessionStorage entre navigations; los marcadores
 * de one-shot viven en localStorage con cooldown (portal ↔ tenant sin sesión).
 */

import { resolvePostLoginPath } from '../utils/postLoginRedirect';

export const RESUME_ATTEMPT_KEY = 'wms_tiger_resume_attempted';
export const TENANT_REDIRECT_TS_KEY = 'wms_tiger_portal_tenant_redirect_ts';
export const TENANT_FRONT_ORIGIN_KEY = 'tiger_tenant_front_origin';
export const MOBILE_REFRESH_TOKEN_KEY = 'wms_mobile_refresh_token';
/** Query: tenant sin sesión → portal; no auto-redirigir al tenant (evita loop splash/login). */
export const PORTAL_SKIP_RESUME_QUERY = 'portal_skip_resume';
export const LOGIN_TRANSITION_PROGRESS_KEY = 'wms_login_transition_progress_v1';

/** Tras redirect portal→tenant, no reintentar resume en Gateway por este lapso. */
const TENANT_REDIRECT_COOLDOWN_MS = Number(
  process.env.REACT_APP_TIGER_RESUME_COOLDOWN_MS || '45000'
);
const SESSION_FETCH_TIMEOUT_MS = Number(
  process.env.REACT_APP_AUTH_SESSION_TIMEOUT_MS
  || process.env.REACT_APP_TIGER_RESUME_FETCH_TIMEOUT_MS
  || '8000'
);
/** Techo total del resume en Portal (anti-hang). Piso 12s. */
export const RESUME_DEADLINE_MS = Math.max(
  12_000,
  Number(process.env.REACT_APP_TIGER_RESUME_DEADLINE_MS || '12000')
);

function startTransitionBeforeRedirect(win) {
  if (!win?.sessionStorage) return;
  try {
    const current = Number(win.sessionStorage.getItem(LOGIN_TRANSITION_PROGRESS_KEY)) || 0;
    if (current > 0) return;
    win.sessionStorage.setItem(LOGIN_TRANSITION_PROGRESS_KEY, '4');
  } catch {
    /* ignore */
  }
}

function redirectWithTransition(win, url, signal) {
  if (signal?.aborted) return false;
  startTransitionBeforeRedirect(win);
  win.location.replace(url);
  return true;
}

const WMS_API_BASE = String(
  process.env.REACT_APP_WMS_API_BASE || '/api/backend'
).replace(/\/+$/, '');

const SESSION_PATH = {
  validateAccess: '/auth_api/custom/token/validate',
  validateRefresh: '/auth_api/custom/refresh_token/validate',
  refreshAccess: '/auth_api/custom/token',
};

const SESSION_HEADERS = {
  Accept: 'application/json',
  'X-Custom-Request': 'connection',
  'X-App-Origin': 'WMS_NEW',
  'X-Client-ID': process.env.REACT_APP_X_CLIENT_ID_AUTH,
};

/**
 * @param {string} [search]
 */
export function parseTigerResumeQuery(search = '') {
  const params = new URLSearchParams(search);
  return {
    loggedOut: params.get('logged_out') === '1',
    skipResume: params.get(PORTAL_SKIP_RESUME_QUERY) === '1',
    tigerSim: params.get('tigerSim') === '1',
  };
}

/**
 * @param {Storage|null|undefined} localStorageRef
 */
export function recentlyRedirectedToTenant(localStorageRef) {
  if (!localStorageRef) return false;
  try {
    const ts = Number(localStorageRef.getItem(TENANT_REDIRECT_TS_KEY) || 0);
    return ts > 0 && Date.now() - ts < TENANT_REDIRECT_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markTenantRedirect(localStorageRef) {
  try {
    localStorageRef?.setItem(TENANT_REDIRECT_TS_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function clearResumeRedirectMarks(stores = {}) {
  const local = stores.localStorage
    ?? (typeof window !== 'undefined' ? window.localStorage : null);
  const session = stores.sessionStorage
    ?? (typeof window !== 'undefined' ? window.sessionStorage : null);
  try {
    local?.removeItem(TENANT_REDIRECT_TS_KEY);
    local?.removeItem(RESUME_ATTEMPT_KEY);
  } catch {
    /* ignore */
  }
  try {
    session?.removeItem(RESUME_ATTEMPT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Limpia artefactos que disparan resume/loader tras logout o bounce sin sesión.
 * @param {{ localStorage?: Storage|null, sessionStorage?: Storage|null }} [stores]
 */
export function clearTigerPortalResumeArtifacts(stores = {}) {
  const local = stores.localStorage
    ?? (typeof window !== 'undefined' ? window.localStorage : null);
  const session = stores.sessionStorage
    ?? (typeof window !== 'undefined' ? window.sessionStorage : null);
  try {
    local?.removeItem(TENANT_FRONT_ORIGIN_KEY);
    local?.removeItem(MOBILE_REFRESH_TOKEN_KEY);
    local?.removeItem('wms_portal_handoff_res_v1');
  } catch {
    /* ignore */
  }
  try {
    session?.removeItem('wms_portal_handoff_res_v1');
  } catch {
    /* ignore */
  }
  clearResumeRedirectMarks({ localStorage: local, sessionStorage: session });
}

export function clearTigerTenantFrontOrigin(stores = {}) {
  const local = stores.localStorage
    ?? (typeof window !== 'undefined' ? window.localStorage : null);
  try {
    local?.removeItem(TENANT_FRONT_ORIGIN_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Entrada LoginPortal: reset loader stale + decidir si corre resume async.
 *
 * - logged_out=1 → limpia lifeline (logout real).
 * - portal_skip_resume=1 → no auto-redirigir en esta carga; **conserva** refresh y
 *   tiger_tenant_front_origin (solo limpia marcas anti-loop).
 *
 * @param {string} [search]
 * @returns {{ runResume: boolean }}
 */
export function prepareLoginPortalEntry(search = '') {
  const query = parseTigerResumeQuery(search);

  if (query.loggedOut) {
    clearTigerPortalResumeArtifacts();
    return { runResume: false };
  }

  if (query.skipResume) {
    // Bounce "sin sesión aparente": no destruir lifeline; solo anti-loop.
    clearResumeRedirectMarks();
    return { runResume: false };
  }

  if (typeof window !== 'undefined' && recentlyRedirectedToTenant(window.localStorage)) {
    return { runResume: false };
  }

  return { runResume: true };
}

/**
 * @param {Storage|null|undefined} storage
 */
export function readMobileRefreshToken(storage) {
  if (!storage) return '';
  try {
    return String(storage.getItem(MOBILE_REFRESH_TOKEN_KEY) || '').trim();
  } catch {
    return '';
  }
}

/**
 * @param {string} origin
 */
export function persistTigerTenantFrontOrigin(origin) {
  if (typeof window === 'undefined') return;
  const value = String(origin || '').trim().replace(/\/+$/, '');
  if (!value || !/^https?:\/\//i.test(value)) return;
  try {
    window.localStorage.setItem(TENANT_FRONT_ORIGIN_KEY, value);
    clearResumeRedirectMarks();
  } catch {
    /* quota */
  }
}

export function readTigerTenantFrontOrigin() {
  if (typeof window === 'undefined') return '';
  try {
    return String(window.localStorage.getItem(TENANT_FRONT_ORIGIN_KEY) || '')
      .trim()
      .replace(/\/+$/, '');
  } catch {
    return '';
  }
}

/**
 * @param {{
 *   isTiger?: boolean,
 *   loggedOut?: boolean,
 *   skipResume?: boolean,
 *   localStorage?: Storage,
 *   hasCookiesHint?: boolean,
 * }} ctx
 */
export function shouldAttemptTigerSessionResume(ctx) {
  if (!ctx?.isTiger) return false;
  if (ctx.loggedOut || ctx.skipResume) return false;
  if (recentlyRedirectedToTenant(ctx.localStorage)) return false;
  const refresh = readMobileRefreshToken(ctx.localStorage);
  if (refresh) return true;
  if (ctx.hasCookiesHint) return true;
  const tenantOrigin = readTigerTenantFrontOrigin();
  if (tenantOrigin) return true;
  return false;
}

function isAuthEnvelopeOk(body) {
  if (!body || typeof body !== 'object') return false;
  const status = String(body.status ?? '').trim().toLowerCase();
  if (status && status !== 'success') return false;
  return true;
}

async function parseJsonResponse(res) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * @param {typeof fetch} fetchFn
 * @param {string} path
 * @param {'GET'|'POST'} method
 * @param {object|null} [body]
 * @param {Record<string, string>} [extraHeaders]
 */
async function sessionFetch(fetchFn, path, method = 'GET', body = null, extraHeaders = {}) {
  const headers = { ...SESSION_HEADERS, ...extraHeaders };
  if (method === 'POST' && body) {
    headers['Content-Type'] = 'application/json';
  }
  const controller = new AbortController();
  const timerId = setTimeout(() => controller.abort(), SESSION_FETCH_TIMEOUT_MS);
  try {
    const res = await fetchFn(`${WMS_API_BASE}${path}`, {
      method,
      headers,
      credentials: 'include',
      signal: controller.signal,
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const envelope = await parseJsonResponse(res);
    return { res, envelope, ok: res.ok && isAuthEnvelopeOk(envelope) };
  } catch {
    return { res: null, envelope: null, ok: false };
  } finally {
    clearTimeout(timerId);
  }
}

function handoffNeedsProfileSelection(data) {
  if (!data || typeof data !== 'object') return false;
  const root = data.data && typeof data.data === 'object' ? data.data : data;
  if (root.requiere_seleccion_perfil === true || root.requiere_seleccion_perfil === 1) {
    return true;
  }
  if (String(root.modo_acceso || '').toLowerCase() === 'pendiente_seleccion_perfil') {
    return true;
  }
  const total = Number(root.total_perfiles_disponibles);
  if (Number.isFinite(total) && total > 1) return true;
  if (Array.isArray(root.perfiles_disponibles) && root.perfiles_disponibles.length > 1) {
    return true;
  }
  if (root.perfil_propio && root.perfil_prestado) return true;
  return false;
}

function extractRefreshFromEnvelope(envelope) {
  if (!envelope || typeof envelope !== 'object') return '';
  const root = envelope.data && typeof envelope.data === 'object' ? envelope.data : envelope;
  const raw =
    root.refresh_token
    ?? root.refreshToken
    ?? envelope.refresh_token
    ?? envelope.refreshToken
    ?? null;
  return String(raw ?? '').trim();
}

function persistRefreshInStorage(localStorageRef, token) {
  const value = String(token ?? '').trim();
  if (!value || !localStorageRef) return;
  try {
    localStorageRef.setItem(MOBILE_REFRESH_TOKEN_KEY, value);
  } catch {
    /* ignore */
  }
}

function clearMobileRefreshArtifactsOnly(localStorageRef) {
  try {
    localStorageRef?.removeItem(MOBILE_REFRESH_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

function isDefinitiveRefreshFailure(status) {
  const code = Number(status);
  return code === 401 || code === 403;
}

/**
 * @param {typeof fetch} fetchFn
 * @param {string} refreshToken
 * @param {Storage|null|undefined} localStorageRef
 * @returns {Promise<{ ok: boolean, definitiveInvalid: boolean }>}
 */
async function tryMobileRefresh(fetchFn, refreshToken, localStorageRef) {
  const token = String(refreshToken || '').trim();
  if (!token) return { ok: false, definitiveInvalid: false };

  const probe = await sessionFetch(fetchFn, SESSION_PATH.validateRefresh, 'GET', null, {
    'Refresh-Token': token,
  });
  if (!probe.ok) {
    const post = await sessionFetch(
      fetchFn,
      SESSION_PATH.refreshAccess,
      'POST',
      { refresh_token: token },
      { 'Refresh-Token': token },
    );
    const rotated = extractRefreshFromEnvelope(post.envelope);
    if (rotated) persistRefreshInStorage(localStorageRef, rotated);
    const status = post.res?.status ?? probe.res?.status ?? 0;
    return {
      ok: post.ok,
      definitiveInvalid: !post.ok && isDefinitiveRefreshFailure(status),
    };
  }

  const post = await sessionFetch(
    fetchFn,
    SESSION_PATH.refreshAccess,
    'POST',
    { refresh_token: token },
    { 'Refresh-Token': token },
  );
  const rotated = extractRefreshFromEnvelope(post.envelope);
  if (rotated) persistRefreshInStorage(localStorageRef, rotated);
  const status = post.res?.status ?? 0;
  return {
    ok: post.ok,
    definitiveInvalid: !post.ok && isDefinitiveRefreshFailure(status),
  };
}

/**
 * Intenta validate access primero; si falla y hay refresh, ejecuta cadena refresh existente.
 * @param {typeof fetch} fetchFn
 * @param {string} refreshToken
 * @param {Storage|null|undefined} localStorageRef
 * @returns {Promise<{
 *   access: Awaited<ReturnType<typeof sessionFetch>>,
 *   renewed?: { ok: boolean, definitiveInvalid: boolean },
 * }>}
 */
async function resolveSessionAccessForResume(fetchFn, refreshToken, localStorageRef) {
  let access = await sessionFetch(fetchFn, SESSION_PATH.validateAccess, 'GET');
  if (access.ok) {
    return { access };
  }

  const refresh = String(refreshToken || '').trim();
  if (!refresh) {
    return { access };
  }

  const renewed = await tryMobileRefresh(fetchFn, refresh, localStorageRef);
  if (!renewed.ok) {
    return { access, renewed };
  }

  access = await sessionFetch(fetchFn, SESSION_PATH.validateAccess, 'GET');
  return { access, renewed };
}

function extractSessionDataFromAccess(access) {
  if (!access?.ok) return null;
  return access.envelope?.data && typeof access.envelope.data === 'object'
    ? access.envelope.data
    : access.envelope;
}

function buildResumeTargetPath(data, deps, win) {
  let target = resolvePostLoginPath(data) || '/dashboard';
  // Anti-loop: tenant `/` re-resuelve modulo_inicio; no dejar resume en `/`.
  if (target === '/') target = '/dashboard';
  if (deps.tigerSim || parseTigerResumeQuery(win.location.search).tigerSim) {
    const u = new URL(target, win.location.origin);
    u.searchParams.set('tigerSim', '1');
    target = `${u.pathname}${u.search}${u.hash}`;
  }
  return target;
}

function redirectResumeToTenant(win, tenantOrigin, sessionData, deps, localStorageRef, signal) {
  if (signal?.aborted) return { resumed: false, reason: 'aborted' };
  if (handoffNeedsProfileSelection(sessionData)) {
    markTenantRedirect(localStorageRef);
    const profilePath = deps.tigerSim || parseTigerResumeQuery(win.location.search).tigerSim
      ? '/login?step=profile&tigerSim=1'
      : '/login?step=profile';
    if (!redirectWithTransition(win, `${tenantOrigin}${profilePath}`, signal)) {
      return { resumed: false, reason: 'aborted' };
    }
    return { resumed: true, reason: 'profile' };
  }
  let target = resolvePostLoginPath(sessionData) || '/dashboard';
  if (target === '/') target = '/dashboard';
  markTenantRedirect(localStorageRef);
  if (!redirectWithTransition(win, `${tenantOrigin}${target}`, signal)) {
    return { resumed: false, reason: 'aborted' };
  }
  return { resumed: true, reason: 'redirect_tenant' };
}

/**
 * @param {{
 *   fetchFn?: typeof fetch,
 *   windowRef?: Window,
 *   localStorage?: Storage,
 *   sessionStorage?: Storage,
 *   tigerSim?: boolean,
 *   signal?: AbortSignal,
 * }} [deps]
 * @returns {Promise<{ resumed: boolean, reason?: string }>}
 */
export async function attemptTigerSessionResume(deps = {}) {
  const win = deps.windowRef || (typeof window !== 'undefined' ? window : null);
  if (!win) return { resumed: false, reason: 'no_window' };
  const signal = deps.signal;

  const localStorageRef = deps.localStorage || win.localStorage;
  const resumeQuery = parseTigerResumeQuery(win.location.search);

  if (!shouldAttemptTigerSessionResume({
    isTiger: true,
    loggedOut: resumeQuery.loggedOut,
    skipResume: resumeQuery.skipResume,
    localStorage: localStorageRef,
  })) {
    return { resumed: false, reason: 'skip' };
  }

  if (signal?.aborted) return { resumed: false, reason: 'aborted' };

  const currentOrigin = String(win.location.origin || '').replace(/\/+$/, '');
  const tenantOrigin = readTigerTenantFrontOrigin();
  const refresh = readMobileRefreshToken(localStorageRef);
  const fetchFn = deps.fetchFn || (typeof win.fetch === 'function' ? win.fetch.bind(win) : null);

  if (tenantOrigin && tenantOrigin !== currentOrigin) {
    if (refresh && fetchFn) {
      const { access, renewed } = await resolveSessionAccessForResume(
        fetchFn,
        refresh,
        localStorageRef,
      );
      if (signal?.aborted) return { resumed: false, reason: 'aborted' };
      if (access.ok) {
        const sessionData = extractSessionDataFromAccess(access);
        return redirectResumeToTenant(
          win,
          tenantOrigin,
          sessionData,
          deps,
          localStorageRef,
          signal,
        );
      }
      clearResumeRedirectMarks({
        localStorage: localStorageRef,
        sessionStorage: deps.sessionStorage || win.sessionStorage,
      });
      if (renewed?.definitiveInvalid) {
        clearMobileRefreshArtifactsOnly(localStorageRef);
      }
      return { resumed: false, reason: renewed ? 'refresh_failed' : 'validate_failed' };
    }
    if (!refresh) {
      // Sin refresh en origen Portal: no forzar hub. El tenant en `/` resuelve modulo_inicio.
      if (signal?.aborted) return { resumed: false, reason: 'aborted' };
      markTenantRedirect(localStorageRef);
      if (!redirectWithTransition(win, `${tenantOrigin}/`, signal)) {
        return { resumed: false, reason: 'aborted' };
      }
      return { resumed: true, reason: 'redirect_tenant' };
    }
  }

  if (!fetchFn) {
    return { resumed: false, reason: 'no_fetch' };
  }

  const { access, renewed } = await resolveSessionAccessForResume(
    fetchFn,
    refresh,
    localStorageRef,
  );
  if (signal?.aborted) return { resumed: false, reason: 'aborted' };
  if (!access.ok) {
    if (!refresh) {
      return { resumed: false, reason: 'no_session' };
    }
    clearResumeRedirectMarks({
      localStorage: localStorageRef,
      sessionStorage: deps.sessionStorage || win.sessionStorage,
    });
    if (renewed?.definitiveInvalid) {
      clearMobileRefreshArtifactsOnly(localStorageRef);
    }
    return { resumed: false, reason: renewed ? 'refresh_failed' : 'validate_failed' };
  }

  const data = extractSessionDataFromAccess(access);
  const target = buildResumeTargetPath(data, deps, win);

  if (handoffNeedsProfileSelection(data)) {
    const profileTarget = deps.tigerSim ? '/login?step=profile&tigerSim=1' : '/login?step=profile';
    if (!redirectWithTransition(win, profileTarget, signal)) {
      return { resumed: false, reason: 'aborted' };
    }
    return { resumed: true, reason: 'profile' };
  }

  if (!redirectWithTransition(win, target, signal)) {
    return { resumed: false, reason: 'aborted' };
  }
  return { resumed: true, reason: 'dashboard' };
}
